import { ChangeEvent, useState } from "react";
import { Form } from "react-bootstrap";
import TextField from "@components/FormsV2/TextField/TextField";
import FormNumberFieldV2 from "@components/FormsV2/FormNumberFieldV2/FormNumberFieldV2";
import GenericToggle from "@components/FormsV2/GenericToggle/GenericToggle";
import RequiredReactSelect from "@components/FormsV2/RequiredReactSelect/RequiredReactSelect";
import { TriggersTable } from "./TriggersTable";
import { ApproversSection } from "../ApproversSection/ApproversSection";
import {
  APPROVAL_KIND_OPTIONS,
  type ApprovalLevelApprover,
  type ApprovalLevelFormValues,
  type ApprovalLevelKind,
  type ApprovalTrigger,
} from "../types/ApprovalLevel";
import "./style.css";
import type {
  AvailableUser,
  AvailableLob,
} from "../ApproversSection/ApproversSection";

export type {
  AvailableUser,
  AvailableLob,
} from "../ApproversSection/ApproversSection";

export interface ApprovalLevelFormProps {
  id: string;
  initialValues: ApprovalLevelFormValues;
  availableUsers: AvailableUser[];
  availableLobs: AvailableLob[];
  onSubmit: (values: ApprovalLevelFormValues) => void;
  isSubmitting?: boolean;
}

export const ApprovalLevelForm = ({
  id,
  initialValues,
  availableUsers,
  availableLobs,
  onSubmit,
  isSubmitting: _isSubmitting,
}: ApprovalLevelFormProps) => {
  const [values, setValues] = useState<ApprovalLevelFormValues>(initialValues);
  const update = <K extends keyof ApprovalLevelFormValues>(
    key: K,
    val: ApprovalLevelFormValues[K]
  ) => setValues((prev) => ({ ...prev, [key]: val }));

  return (
    <Form
      id={id}
      onSubmit={(e) => {
        e.preventDefault();
        onSubmit(values);
      }}
    >
      <div className="adminFormSectionTitle">Identifier & Settings</div>
      <div className="row">
        <div className="col-sm-6">
          <Form.Label htmlFor="al-name">* Name</Form.Label>
          <TextField
            label={null}
            id="al-name"
            value={values.name}
            placeholder="Enter a name"
            onChange={(e: ChangeEvent<HTMLInputElement>) =>
              update("name", e.target.value)
            }
            required
          />
        </div>
        <div className="col-sm-3">
          <RequiredReactSelect
            label="* Kind"
            value={APPROVAL_KIND_OPTIONS.find((o) => o.value === values.kind)}
            onChange={(opt: any) => {
              const kind = (opt?.value ?? "business") as ApprovalLevelKind;
              update("kind", kind);
            }}
            options={APPROVAL_KIND_OPTIONS}
            required
          />
        </div>
        <div className="col-sm-3">
          <FormNumberFieldV2
            label="* Approvals Required"
            id="al-signatures"
            value={values.signaturesRequired}
            min={1}
            placeholder="Enter a number"
            onChange={(e: ChangeEvent<HTMLInputElement>) =>
              update(
                "signaturesRequired",
                e.target.value === "" ? 1 : Number(e.target.value)
              )
            }
            required
          />
        </div>
      </div>
      <div className="row">
        <div className="col-sm-4 approvalLevelToggleCell">
          <Form.Label>May Submitter Also Approve?</Form.Label>
          <GenericToggle
            key={`smay-${initialValues.submitterMayApprove}`}
            initialValueOfIsOn={values.submitterMayApprove}
            onToggle={(v: boolean) => update("submitterMayApprove", v)}
            onText="Yes"
            offText="No"
            hasText
          />
        </div>
        <div className="col-sm-4 approvalLevelToggleCell">
          <Form.Label>Limit Approval to Submitter's Team?</Form.Label>
          <GenericToggle
            key={`lim-${initialValues.limitApprovalsToTeam}`}
            initialValueOfIsOn={values.limitApprovalsToTeam}
            onToggle={(v: boolean) => update("limitApprovalsToTeam", v)}
            onText="Yes"
            offText="No"
            hasText
          />
        </div>
      </div>

      <div className="adminFormSectionTitle">Triggers</div>
      <TriggersTable
        triggers={values.triggers}
        onChange={(triggers: ApprovalTrigger[]) => update("triggers", triggers)}
      />

      <ApproversSection
        kind={values.kind}
        approvers={values.approvers}
        availableUsers={availableUsers}
        availableLobs={availableLobs}
        onChange={(approvers: ApprovalLevelApprover[]) =>
          update("approvers", approvers)
        }
      />
    </Form>
  );
};
